class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname.includes("cart");
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) return;
    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
        convertRecommendedPrices();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
    if (this.isOpen) {
      convertRecommendedPrices();
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });
  }

  renderContents(parsedState, isClosedCart = true) {
    const replacements = this.getSectionsToRender().map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return null;
      const html = this.getSectionInnerHTML(
        parsedState.sections?.[section.id],
        section.selector,
      );
      if (html === undefined) {
        throw new Error(`Unable to render cart section: ${section.id}`);
      }
      return { element: sectionElement, html };
    });
    replacements.forEach((replacement) => {
      if (replacement) replacement.element.innerHTML = replacement.html;
    });
    this.classList.toggle("wt-cart--empty", Boolean(this.querySelector(".lux-is-cart-empty")));

    if (isClosedCart) {
      setTimeout(() => {
        if (!this.isOpen) this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (typeof html !== "string") return undefined;
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
    ];
    if (document.getElementById("cart-icon-bubble")) {
      sections.push({ id: "cart-icon-bubble" });
    }
    return sections;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  disconnectedCallback() {
    super.disconnectedCallback();
    this.refreshController?.abort();
  }

  onCartUpdate(event) {
    const drawer = this.closest("cart-drawer");
    if (!drawer) return super.onCartUpdate();

    this.refreshController?.abort();
    // Add-to-cart components render their bundled drawer section themselves.
    if (event?.cartData?.sections?.["cart-drawer"]) return;

    const controller = new AbortController();
    this.refreshController = controller;
    return fetch(`${routes.cart_url}?section_id=cart-drawer`, {
      signal: controller.signal,
    })
      .then((response) => response.text())
      .then((html) => {
        if (!this.isConnected || controller.signal.aborted) return;
        drawer.renderContents({ sections: { "cart-drawer": html } }, false);
      })
      .catch((error) => {
        if (error.name !== "AbortError") console.error(error);
      });
  }

  updateQuantity(line, quantity, name) {
    // A pending refresh must not restore the cart from before this change.
    this.refreshController?.abort();
    return super.updateQuantity(line, quantity, name);
  }

  getSectionsToRender() {
    if (!this.closest("cart-drawer")) return super.getSectionsToRender();
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ].filter((section) => document.getElementById(section.id));
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
